// Canonical host: the app must never run on www — sessionStorage (financials
// unlock) and the QBO OAuth redirect-origin allowlist are all apex-only.
if (window.location.hostname === "www.nycsuite.io") {
  window.location.replace(
    `https://nycsuite.io${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import E2EJobsFixture from "./test/E2EJobsFixture.jsx";
import E2EQuotesFixture from "./test/E2EQuotesFixture.jsx";
import E2ECrmFixture from "./test/E2ECrmFixture.jsx";
import E2ETopbarFixture from "./test/E2ETopbarFixture.jsx";
import E2EJobsListFixture from "./test/E2EJobsListFixture.jsx";
import E2EJobDetailFixture from "./test/E2EJobDetailFixture.jsx";
import E2EDashboardFixture from "./test/E2EDashboardFixture.jsx";
import E2EScheduleFixture from "./test/E2EScheduleFixture.jsx";
import CrewCardPage, { parseCardRoute } from "./crewcard/CrewCardPage.jsx";
import "./responsive.css";
import { localE2EMode } from "./test/e2eMode.js";
import { installErrorBuffer } from "./lib/feedback/errorBuffer.js";

// The last few client errors, in memory only, for the Feedback tab's bug
// reports (src/lib/feedback/errorBuffer.js).
installErrorBuffer();


// Surface render errors on-screen instead of leaving the user on a blank
// page. Without this, a single bad prop anywhere in the render tree
// unmounts everything silently. Temporary diagnostic; safe to keep.
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[RootErrorBoundary] render crash:", error, info?.componentStack);
    this.setState({ info });
  }
  render() {
    if (this.state.error) {
      const stack = this.state.info?.componentStack || "(no component stack)";
      return (
        <div style={{
          minHeight: "100vh",
          padding: "32px 24px",
          background: "#F5F3F0",
          color: "#1A1A1A",
          fontFamily: "'DM Mono', monospace",
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>
            App render crashed
          </div>
          <div style={{ marginBottom: 12 }}>{String(this.state.error?.message || this.state.error)}</div>
          <div style={{ marginBottom: 12, opacity: 0.7 }}>Component stack:</div>
          <div>{stack}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Public crew card route (docs/crew-cards/DESIGN.md): /card/{token}. Rendered
// BEFORE the App auth gate; the page fetches its snapshot over HTTPS and never
// touches Firestore or Auth.
const CARD_ROUTE = parseCardRoute(window.location.pathname, window.location.search);
const CrewCardRoot = () => <CrewCardPage route={CARD_ROUTE} />;

const E2E_MODE = localE2EMode();
const Root = CARD_ROUTE ? CrewCardRoot : E2E_MODE === "jobs" ? E2EJobsFixture : E2E_MODE === "quotes" ? E2EQuotesFixture : E2E_MODE === "crm" ? E2ECrmFixture : E2E_MODE === "topbar" ? E2ETopbarFixture : E2E_MODE === "jobslist" ? E2EJobsListFixture : E2E_MODE === "jobdetail" ? E2EJobDetailFixture : E2E_MODE === "dashboard" ? E2EDashboardFixture : E2E_MODE === "schedule" ? E2EScheduleFixture : App;

ReactDOM.createRoot(document.getElementById("root")).render(
  <RootErrorBoundary>
    <Root />
  </RootErrorBoundary>
);
